import {
  apiDelete,
  apiListAll,
  apiPatch,
  apiPost,
  type MassnahmeCreate,
  type MassnahmeRow as MassnahmeCoreRow,
} from '@/core/api/resources'
import { composeMassnahmeSelection, splitMassnahmeSelection, findMassnahmeSelectionByNummer } from '@/core/lib/massnahme'
import { descriptionFor, subtypeFor } from '@/variants'
import { me, currentVariant } from './session'

export type MassnahmeRow = {
  id: string
  nummer: string
  bezeichnung: string

  durchf_form: string
  ue_anzahl: number | null
  kosten: string | null
  zeitl_umfang_woche: string | null
  start_zulassung: string | null
  ende_zulassung: string | null
  inhalt: string | null
  durchf_ort: string | null
  abschluss: string | null
  ziele_und_inhalt: string | null

  ansprechpartner: string | null
  anwesenheit_stichtag?: string
  aktivierung_zustaendig?: string | null
}

export async function listMassnahmenFull(organisationId: string): Promise<MassnahmeRow[]> {
  const { subtype } = descriptionFor(currentVariant())
  if (subtype) return subtype.listMassnahmenFull(organisationId)
  const rows = await apiListAll('/massnahme', {
    organisation_id: organisationId,
    order: 'nummer',
  })
  return rows.map(fromCoreRow)
}

function fromCoreRow(m: MassnahmeCoreRow): MassnahmeRow {
  return {
    anwesenheit_stichtag: m.anwesenheit_stichtag,
    aktivierung_zustaendig: m.aktivierung_zustaendig,
    id: m.id,
    nummer: m.nummer,
    bezeichnung: m.bezeichnung,
    durchf_form: m.durchf_form,
    inhalt: m.inhalt,
    durchf_ort: m.durchf_ort,
    ansprechpartner: m.ansprechpartner,
    ue_anzahl: null,
    kosten: null,
    zeitl_umfang_woche: null,
    start_zulassung: null,
    ende_zulassung: null,
    abschluss: null,
    ziele_und_inhalt: null,
  }
}

export type { MassnahmeCreate } from '@/core/api/resources'
export type MassnahmePatch = Partial<Omit<MassnahmeRow, 'id'>>

export async function createMassnahme(
  organisationId: string,
  patch: MassnahmeCreate,
): Promise<MassnahmeRow> {

  const variant = descriptionFor(currentVariant())
  if (variant.subtype) return variant.subtype.createMassnahme(organisationId, patch)

  const created = await apiPost('/massnahme', {
    ...variant.massnahmeDefaults,
    ...patch,
    organisation_id: organisationId,
  })
  return fromCoreRow(created)
}

export async function updateMassnahme(id: string, patch: MassnahmePatch): Promise<void> {

  const { subtype } = descriptionFor(currentVariant())
  if (subtype) return subtype.updateMassnahme(id, patch)
  await apiPatch('/massnahme/{id}', { id }, patch)
}

export async function deleteMassnahme(id: string): Promise<void> {
  await apiDelete('/massnahme/{id}', { id })
}

export type MassnahmeUeBudget = { ueAnzahl: number | null; label: string }

export async function getMassnahmeUeBudget(massnahmeId: string): Promise<MassnahmeUeBudget | null> {

  return subtypeFor(currentVariant()).getMassnahmeUeBudget(massnahmeId)
}

export type MassnahmeOption = { id: string; label: string; ue_anzahl: number }

export async function listMassnahmen(): Promise<MassnahmeOption[]> {
  const { organisationId, variant } = await me()
  return subtypeFor(variant).listMassnahmen(organisationId)
}

export type MassnahmeTbFields = {
  id: string
  zeitl_umfang: string
  ue_anzahl: number
}

export async function getMassnahmeTbFields(selection: string): Promise<MassnahmeTbFields | null> {
  const { organisationId, variant } = await me()

  const { nummer } = splitMassnahmeSelection(selection)

  return subtypeFor(variant).getMassnahmeTbFields(organisationId, nummer)
}

export async function getMassnahmeSelectionByNummer(nummer: string): Promise<string | null> {
  const { organisationId } = await me()
  const rows = await apiListAll('/massnahme', { organisation_id: organisationId })
  return findMassnahmeSelectionByNummer(
    nummer,
    rows.map((m) => composeMassnahmeSelection(String(m.nummer ?? ''), String(m.bezeichnung ?? ''))),
  )
}
